import { useCallback, useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams, useTerritories } from "../contexts";
import { getQueryType, getRequestOptions, saveFile } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    Shipment,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
    QueryParams,
    MutationResult,
    CreateShipmentRequest,
    ShipmentDashboardData,
    Tissue,
    QueryResult,
} from "../models";
import { Operation } from "rfc6902";

interface IUseShipmentQueryParams extends Omit<QueryHookParams, "queryType"> {
    tissueId?: string | number;
    queryType?: QueryType | "dashboard";
}

interface IUseShipmentQuery extends Omit<IUseQueryPaged<Shipment>, "createMutation"> {
    createMutation: MutationResult<Shipment, unknown, CreateShipmentRequest>;
    latestForTissueQuery: QueryResult<Shipment>;
    downloadCsv: (tissue: Tissue) => Promise<void>;
    dashboardQuery: QueryResult<ShipmentDashboardData>;
    packMutation: MutationResult<Shipment, unknown, void>;
    receiveMutation: MutationResult<Shipment, unknown, void>;
    unpackMutation: MutationResult<Shipment, unknown, void>;
}

export const useShipmentQuery = (params?: IUseShipmentQueryParams): IUseShipmentQuery => {
    const shipmentId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get, getBlob, post, patch, del } = useAxios();
    const { selectedOfficeIds } = useTerritories();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Shipments);
    const url = Endpoints.Shipments;
    const queryType = params?.queryType === "dashboard" ? "dashboard" : getQueryType(shipmentId, params?.queryType);
    const queryParamsWithArgs: QueryParams = {
        ...queryParams,
        tissueId: params?.tissueId?.toString() ?? "",
        officeIds: selectedOfficeIds.join(","),
    };

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Shipment>>(getRequestOptions(url, config, queryParamsWithArgs, sortParams, pageParams)),
        queryKey: queryKeys.shipments.pagedList({ ...queryParamsWithArgs, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Shipment>>(
                getRequestOptions(url, config, queryParamsWithArgs, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.shipments.list({ ...queryParamsWithArgs, ...sortParams }),
        placeholderData: [],
        enabled: isAuthorized && queryType === QueryType.List && !!params?.tissueId,
    });

    const dashboardQuery = useGenericQuery({
        queryFn: (config) =>
            get<ShipmentDashboardData>(getRequestOptions(`${url}/dashboard`, config, queryParamsWithArgs)),
        queryKey: queryKeys.dashboards.shipment(queryParamsWithArgs),
        placeholderData: {} as ShipmentDashboardData,
        enabled: isAuthorized && queryType === "dashboard",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const latestForTissueQuery = useGenericQuery({
        queryFn: () => get<Shipment>({ url: `${url}/latest?tissueId=${params?.tissueId}` }),
        queryKey: queryKeys.shipments.latestForTissue(params?.tissueId ?? ""),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.tissueId,
        staleTime: 5000,
        cacheTime: 5000,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Shipment>({ url: `${url}/${shipmentId}`, config }),
        queryKey: queryKeys.shipments.detail(shipmentId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
        staleTime: 5000,
        cacheTime: 5000,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: CreateShipmentRequest) => post<CreateShipmentRequest, Shipment>({ url, data }),
        queryKey: queryKeys.shipments.all(),
        keysToInvalidate: [queryKeys.shipments.lists(), queryKeys.shipments.pagedLists(), queryKeys.tissues.all()],
        successToast: "Shipment created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], Shipment>({ url: `${url}/${shipmentId}`, data }),
        queryKey: queryKeys.shipments.detail(shipmentId),
        keysToInvalidate: [queryKeys.shipments.lists(), queryKeys.shipments.pagedLists(), queryKeys.orders.all()],
        successToast: "Shipment updated",
    });

    const packMutation = useGenericMutation({
        mutationFn: () => patch<void, Shipment>({ url: `${url}/${shipmentId}/pack` }),
        queryKey: queryKeys.shipments.detail(shipmentId),
        keysToInvalidate: [
            queryKeys.shipments.lists(),
            queryKeys.shipments.pagedLists(),
            queryKeys.orders.all(),
            queryKeys.tissues.all(),
        ],
        successToast: "Shipment packed",
    });

    const receiveMutation = useGenericMutation({
        mutationFn: () => patch<void, Shipment>({ url: `${url}/${shipmentId}/receive` }),
        queryKey: queryKeys.shipments.detail(shipmentId),
        keysToInvalidate: [
            queryKeys.shipments.lists(),
            queryKeys.shipments.pagedLists(),
            queryKeys.orders.all(),
            queryKeys.tissues.all(),
        ],
        successToast: "Shipment received",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${shipmentId}` }),
        queryKey: queryKeys.shipments.detail(shipmentId),
        keysToInvalidate: [queryKeys.shipments.lists(), queryKeys.shipments.pagedLists()],
        successToast: "Shipment deleted",
    });

    const downloadCsv = useCallback(
        async (tissue: Tissue) => {
            const blob = await getBlob({ url: `${url}/export?tissueId=${tissue.id}` });
            saveFile(`${tissue.number}-shipments.csv`, "text/csv", blob);
        },
        [getBlob, url]
    );

    const unpackMutation = useGenericMutation({
        mutationFn: () => patch<void, Shipment>({ url: `${url}/${shipmentId}/unpack` }),
        queryKey: queryKeys.shipments.detail(shipmentId),
        keysToInvalidate: [queryKeys.shipments.lists(), queryKeys.shipments.pagedLists(), queryKeys.orders.all()],
        successToast: "Shipment unpacked",
    });

    return useMemo<IUseShipmentQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            latestForTissueQuery,
            dashboardQuery,
            createMutation,
            updateMutation,
            packMutation,
            receiveMutation,
            deleteMutation,
            downloadCsv,
            unpackMutation,
        }),
        [
            pagedListQuery,
            listQuery,
            detailQuery,
            latestForTissueQuery,
            dashboardQuery,
            createMutation,
            updateMutation,
            packMutation,
            receiveMutation,
            deleteMutation,
            downloadCsv,
            unpackMutation,
        ]
    );
};
